import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { getApiErrorMessage } from '../api/logic/getApiErrorMessage';
import {
  useConfirmQrCode,
  useGetTwoFactorSetup,
  useRequestConfirmationCode,
} from '../api/middle-office/authentication';
import {
  authenticatedSession,
  useAuthApi,
  useAuthData,
} from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserContext';
import { useToast } from '../lib/chakraUi/hooks';
import { useEventHandler } from './useEventHandler';

export const useAuthenticatorModal = (
  isClosable: boolean | undefined,
  successPath: string,
  onDone?: () => void,
  onCloseHandler?: () => void,
) => {
  const { refetch } = useUserData();
  const { setSession } = useAuthApi();
  const session = useAuthData();
  const [clientHash, setClientHash] = useState<string>('');
  const [qrCode, setQrCode] = useState<string>('');
  const [twoFactorMethodModal, setTwoFactorMethodModal] =
    useState<boolean>(true);
  const [scanCode, setScanCode] = useState<boolean>(false);
  const [enterCode, setEnterCode] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<string>('');
  const [twoFactorMethod, setTwoFactorMethod] = useState<string>('mail');
  const [twoFactorAuthCode, setTwoFactorAuthCode] = useState<string>('');
  const toast = useToast();
  const navigate = useNavigate();

  const { twoFactorSetup } = useGetTwoFactorSetup(
    twoFactorMethod === 'app' ? 'app' : '',
  );
  const provisioningUri = twoFactorSetup?.provisioningUri ?? '';
  const authCode = twoFactorSetup?.code ?? '';

  const { requestConfirmationCode } = useRequestConfirmationCode();
  const { confirmQrCode } = useConfirmQrCode();

  const showChooseTwoFactorMethod = useEventHandler((): void => {
    setTwoFactorMethodModal(true);
    setScanCode(false);
    setEnterCode(false);
    setSuccessMsg(false);
  });

  const showScanCode = useEventHandler((): void => {
    setTwoFactorMethodModal(false);
    setScanCode(true);
    setEnterCode(false);
    setSuccessMsg(false);
  });

  const showSuccessMsg = useEventHandler((): void => {
    setTwoFactorMethodModal(false);
    setScanCode(false);
    setEnterCode(false);
    setSuccessMsg(true);
  });

  useEffect(() => {
    showChooseTwoFactorMethod();
  }, []);

  const showEnterCode = useEventHandler(async (resent?: boolean) => {
    if (twoFactorMethod === 'mail') {
      try {
        const res = await requestConfirmationCode({ method: 'mail' });
        setClientHash(res?.data.clientHash ?? '');
        setTwoFactorMethodModal(false);
        setScanCode(false);
        setEnterCode(true);
        setSuccessMsg(false);
        if (resent) {
          toast({
            description: 'The 6-digit code has been re-sent to your email',
            type: 'success',
            title: 'Two-factor verification',
          });
        }
      } catch (e: any) {
        setErrorCode(getApiErrorMessage(e, ''));
      }
    } else {
      setTwoFactorMethodModal(false);
      setScanCode(false);
      setEnterCode(true);
      setSuccessMsg(false);
    }
  });

  const verifyQrCode = useEventHandler(async (): Promise<void> => {
    try {
      const res = await confirmQrCode({
        code: qrCode,
        method: twoFactorMethod,
        clientHash,
      });

      if (res?.data) {
        showSuccessMsg();
        refetch();
        const token = res.meta?.twoFactorToken || '';
        setTwoFactorAuthCode(token);
        if (!isClosable && token && session.jwt) {
          setSession(
            authenticatedSession(session.jwt, session.expiresAt ?? '', token),
          );
        }
      }
    } catch (e: any) {
      setErrorCode(getApiErrorMessage(e, ''));
    }
  });

  const closeAndSetAuth = useEventHandler(async (): Promise<void> => {
    if (onCloseHandler) {
      onCloseHandler();
    }

    showScanCode();

    if (!isClosable) {
      refetch();
      // Only promote to authenticated when there is a setup token and a current
      // first-factor session; otherwise leave the session untouched.
      if (twoFactorAuthCode && session.jwt) {
        setSession(
          authenticatedSession(
            session.jwt,
            session.expiresAt ?? '',
            twoFactorAuthCode,
          ),
        );
      }
      navigate(successPath);
    }

    if (onDone) {
      onDone();
    }
  });

  return {
    provisioningUri,
    qrCode,
    authCode,
    twoFactorMethodModal,
    scanCode,
    enterCode,
    successMsg,
    errorCode,
    twoFactorMethod,
    twoFactorAuthCode,
    setTwoFactorMethod,
    setQrCode,
    setErrorCode,
    showScanCode,
    showEnterCode,
    verifyQrCode,
    closeAndSetAuth,
    showSuccessMsg,
    showChooseTwoFactorMethod,
  };
};
